import type { ReactNode } from "react"
import React from "react"
import type { ChakraProps } from "@chakra-ui/react"
import {
  Heading,
  Button,
  Divider,
  AccordionItem,
  AccordionButton,
  Box,
  Text,
  AccordionPanel,
} from "@chakra-ui/react"
import { Link } from "components/Link"
import { createMarkup } from "utils/createMarkup"
import { ChevronUpIcon, ChevronDownIcon } from "components/SvgIcons"
import { hexToRgba } from "lib/color"
import { colours } from "@shared/ui.design-system"
import {
  Offer,
  PriceTerms,
  OfferType,
  OfferTerms,
  CopyText,
} from "./OfferCard.styles"

type CTA = {
  destination: string
  title: string
  copy: string
}

type Props = ChakraProps & {
  headline: string
  currencySymbol: string
  price?: number
  value?: string
  length: string
  terms?: string
  copy: string
  cta?: CTA
  buttonVariant?: string
  badge?: ReactNode
  expandable?: boolean
  onClick?: React.MouseEventHandler<HTMLButtonElement>
}

const CustomAccordionItem: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <AccordionItem border="none">
    {({ isExpanded }) => (
      <>
        <AccordionButton p="0" m="0">
          <Box flex="1" textAlign="left" pr="-1">
            {isExpanded ? (
              <Text mt={0} variant="book2" textAlign="right">
                Show less
              </Text>
            ) : (
              <Text mt={0} variant="book2" textAlign="right">
                Learn more
              </Text>
            )}
          </Box>
          {isExpanded ? (
            <ChevronUpIcon boxSize="16px" />
          ) : (
            <ChevronDownIcon boxSize="16px" />
          )}
        </AccordionButton>
        <AccordionPanel px={2} py={-1}>
          {children}
        </AccordionPanel>
      </>
    )}
  </AccordionItem>
)

export const OfferCard: React.FC<Props> = ({
  headline,
  currencySymbol,
  price,
  value,
  length,
  terms,
  copy,
  cta,
  buttonVariant = "primary",
  badge,
  expandable = false,
  onClick = undefined,
  ...otherProps
}) => {
  const textMarginTop = terms ? -1 : 0

  return (
    <Offer {...otherProps}>
      <Heading
        as="h3"
        variant="caps3"
        mb="6px"
        lineHeight="2.25rem"
        fontSize="1.5rem"
        w={{ base: "100%", md: "97%", lg: "100%" }}
        dangerouslySetInnerHTML={createMarkup(headline)}
        css={`
          @media (min-width: 768px) and (max-width: 1023px) {
            strong {
              display: block;
            }
          }
        `}
      />
      <Divider
        border="0"
        borderBottom={`solid 1px ${hexToRgba(colours.primary.dark, 0.2)}`}
      />
      <PriceTerms>
        {!!price && (
          <Heading
            as="h4"
            variant="apercu"
            fontWeight="400"
            gridColumn="1"
            fontSize="2.25rem"
            lineHeight="13"
          >
            {currencySymbol}
            {price}
          </Heading>
        )}
        {value && (
          <Heading
            as="h4"
            variant="apercu"
            fontWeight="400"
            gridColumn="1"
            fontSize="2.25rem"
            lineHeight="13"
          >
            <Text letterSpacing="0" mt="0">
              {value}
            </Text>
          </Heading>
        )}
        <OfferType
          variant="acuminLight"
          dangerouslySetInnerHTML={createMarkup(length)}
          css={`
            sup {
              font-size: 9px;
              position: relative;
              top: -8px;
            }
            span {
              color: ${colours.primary.medium};
              font-size: 12px;
              line-height: 16px;
            }
            span:before {
              border-left: 0.25px solid ${colours.transparency.dark};
              content: " ";
              height: 12px;
              margin: -2px 8px 0 4px;
              display: inline-block;
              vertical-align: middle;
            }
            @media (max-width: 1023px) {
              span:before {
                display: none;
              }
              span {
                display: block;
                font-size: 16px;
                line-height: 26px;
              }
            }
          `}
        />
      </PriceTerms>
      {!!badge && badge}
      {expandable ? (
        <CustomAccordionItem>
          {terms && <OfferTerms variant="acuminLight">{terms}</OfferTerms>}
          <CopyText
            variant="acuminLight"
            dangerouslySetInnerHTML={createMarkup(copy)}
          />
        </CustomAccordionItem>
      ) : (
        <>
          {terms && (
            <OfferTerms
              px={1}
              variant="acuminLight"
              dangerouslySetInnerHTML={createMarkup(terms)}
            />
          )}
          <CopyText
            css={`
              @media (max-width: 374px) {
                .mobileOffer {
                  max-width: 9.8rem;
                }
              }
            `}
            mt={textMarginTop}
            mb={-1}
            px={1}
            variant="acuminLight"
            fontSize="1"
            lineHeight="3"
            dangerouslySetInnerHTML={createMarkup(copy)}
          />
        </>
      )}

      {cta && (
        <Button
          mt={{ base: 1, md: 2 }}
          variant={buttonVariant}
          size="medium"
          {...(!onClick && {
            as: Link,
            href: cta?.destination,
            target: "_self",
          })}
          {...(onClick && { onClick })}
          title={cta?.title}
          textTransform="none"
          w="full"
        >
          <Text mt="0" variant="acuminSemiBold" fontSize="1">
            {cta?.copy}
          </Text>
        </Button>
      )}
    </Offer>
  )
}
